import { Divider, Typography, createTheme, styled } from "@mui/material";
import { IEnhancedMenuItem, IHeading, ISeparator, ITest } from "@websential/cosmic-v20";
import React, { FC } from "react";

import ArrowDropDownSharpIcon from "@mui/icons-material/ArrowDropDownSharp";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Link } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ThemeProvider } from "@mui/material/styles";
import { grey } from "@mui/material/colors";

export interface IEnhancedMenuProps extends ITest {
	/**
	 * Menu title text
	 */
	menuTitleText: String;
	/**
	 * Set color of button from given colors
	 */
	menuTitleBackgroundColor: "primary" | "secondary";
	/**
	 * Add icon (if needed) in the start of button before text/label
	 */
	startIcon?: React.ReactNode;
	/**
	 * Make it true/false accordingly to appear Down Arrow Icon on right side of button after text
	 */
	showEndIcon: Boolean;

	/**
	 * Set type of button
	 */
	variant: "text" | "outlined" | "contained";
	/**
	 * Give required Array of items to be render in menu on button click
	 */
	menuItems: (IEnhancedMenuItem | ISeparator | IHeading)[];
	/** Optional second line under the title, e.g. "Payroll Administrator" */
	menuSubtitleText?: string;
	/** Optional third line, e.g. "Last login Sep 21, 2026 · 9:12 AM ET" */
	menuCaptionText?: string;
}
const INK = "#0f2a20";
const ACCENT = "var(--sp-primary-light)";
const HOVER_BG = "#f1f6f3";
export const greyTheme = createTheme({
	palette: {
		primary: {
			main: grey[500],
		},
		secondary: {
			main: grey[800],
		},
	},
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
	backgroundColor: "#e0e0e0",
	color: "#424242",
	"&:hover": {
		backgroundColor: "#9e9e9e",
		color: "#fafafa",
	},
	borderRadius: 5,
}));

const StyledRouterLink = styled(Link)(({ theme }) => ({
	textDecoration: "none",
	color: "#000000",
}));

// const useStyles = styled(() => ({
//   iconButton: {
// backgroundColor: "#e0e0e0",
// color: "#424242",
// "&:hover": {
//   backgroundColor: "#9e9e9e",
//   color: "#fafafa"
// },
// borderRadius: 5,
//   },
//   itemLink: {
//     textDecoration: 'none',
//     color: "#000000"
//   }
// }));

const ProfileTrigger = styled(Button)(() => ({
	gap: 4,
	minWidth: 0,
	// padding: "6px 8px",
	borderRadius: 12,
	textAlign: "left",
	textTransform: "none",
	transition: "background-color 0.15s ease",

	"&:hover": {
		backgroundColor: "rgba(255, 255, 255, 0.06)",
	},
	"&.Mui-focusVisible": {
		outline: `2px solid ${ACCENT}`,
		outlineOffset: 2,
	},

	// Initials avatar
	"& .MuiAvatar-root": {
		backgroundColor: ACCENT,
		color: INK,
		fontSize: "0.95rem!important",
		fontWeight: 700,
		letterSpacing: "0.02em",
		width: "34px!important",
		height: "34px!important",
	},

	"& .MuiButton-startIcon": {
		marginLeft: ".4rem",
	},

	// Chevron
	"&& .MuiButton-endIcon": {
		marginLeft: 6,   // 👈 set the spacing you want between text and chevron
		marginRight: 4,  // cancels the parent's `.MuiButton-icon { margin-right: 8px }`
		color: "rgba(255, 255, 255, 0.6)",
		transition: "transform 0.2s ease, color 0.15s ease",
	},
	"&:hover .MuiButton-endIcon": {
		color: "#ffffff",
	},
	"&[aria-expanded='true'] .MuiButton-endIcon": {
		transform: "rotate(180deg)",
	},

	// Text block
	"& .em-text": {
		flexDirection: "column",
		minWidth: 0,
		lineHeight: 1.25,
	},
	"& .em-text__name": {
		maxWidth: 200,
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		color: "#ffffff",
		fontSize: "0.95rem",
		fontWeight: 700,
	},
	"& .em-text__subtitle": {
		color: "rgba(255, 255, 255, 0.72)",
		fontSize: "0.75rem",
		fontWeight: 500,
	},
	"& .em-text__caption": {
		color: "rgba(255, 255, 255, 0.5)",
		fontSize: "0.7rem",
		fontWeight: 400,
	},
}));

/** "Muhammad Qasim" -> "MQ" */
const getInitials = (name?: string): string =>
	(name ?? "")
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0]?.toUpperCase() ?? "")
		.join("");

/**
 * If the start icon is an <Avatar> with no picture, show initials instead of the
 * default grey person icon. Avatars with a `src` or children are left untouched.
 */
const withInitials = (icon: React.ReactNode): React.ReactNode => {
	if (!React.isValidElement(icon)) return icon;
	const props = icon.props as { src?: string; alt?: string; children?: React.ReactNode };
	if (props.src || props.children || !props.alt) return icon;
	const initials = getInitials(props.alt);
	return initials ? React.cloneElement(icon as React.ReactElement<any>, { children: initials }) : icon;
};

// ---------------------------------------------------------------------------
// Dropdown panel styles (applied via sx so they work across MUI v5–v7)
// ---------------------------------------------------------------------------
const menuSx = {
	"& .MuiPaper-root": {
		minWidth: 220,
		marginTop: "8px",
		borderRadius: "12px",
		border: "1px solid #e6ebe8",
		boxShadow: "0 12px 32px rgba(15, 42, 32, 0.16)",
	},
	"& .MuiList-root": {
		padding: "6px",
	},
	"& .MuiMenuItem-root": {
		minHeight: 38,
		// padding: "8px 12px",
		padding: "0!important",
		borderRadius: "8px",
		color: INK,
		fontSize: "0.9rem",
		fontWeight: 500,
		transition: "background-color 0.15s ease",
	},
	"& .MuiMenuItem-root .MuiTypography-root": {
		fontSize: "inherit",
		fontWeight: "inherit",
	},
	"& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-focusVisible": {
		backgroundColor: HOVER_BG,
	},
	// Headings (disabled items)
	"& .MuiMenuItem-root.Mui-disabled": {
		opacity: 1,
		minHeight: 30,
		color: "#8a948f",
		fontSize: "0.72rem",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
	},
	"& .MuiDivider-root": {
		margin: "6px 4px",
		borderColor: "#e6ebe8",
	},
} as const;


export const EnhancedMenu: FC<IEnhancedMenuProps> = ({
	menuTitleText,
	menuTitleBackgroundColor,
	testId,
	variant,
	startIcon,
	showEndIcon,
	menuItems,
	menuSubtitleText,
	menuCaptionText,
}: IEnhancedMenuProps) => {
	// const classes = useStyles();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const getMenuItem = (
		menuItem: IEnhancedMenuItem | IHeading,
		index: number
	) => {
		let headingItem = menuItem as IHeading;
		let menu = menuItem as IEnhancedMenuItem;
		return !headingItem.isHeading ? (
			<StyledRouterLink
				to={String(menu.navigateTo)}
				key={index}
				data-testid={testId + "-" + menuItem.label + "-link"}
			>
				<MenuItem key={index}>
					<Typography>{menuItem.label}</Typography>
				</MenuItem>
			</StyledRouterLink>
		) : (
			<MenuItem disabled key={index}>
				{menuItem.label}
			</MenuItem>
		);
	};
	return (
		<>
			<ThemeProvider theme={greyTheme}>
				{showEndIcon ? (
					<ProfileTrigger
						data-testid={testId + "-button"}
						startIcon={withInitials(startIcon)}
						endIcon={<KeyboardArrowDownRoundedIcon />}
						variant={variant}
						onClick={handleClick}
						color={menuTitleBackgroundColor}
						aria-haspopup="menu"
						aria-expanded={open}
					>
						{/* Keeps variant="body2" so ProfileMenu's xs "hide name" rule still applies */}
						<Typography variant="body2" component="span" className="em-text">
							<span className="em-text__name">{menuTitleText}</span>
							{menuSubtitleText && <span className="em-text__subtitle">{menuSubtitleText}</span>}
							{menuCaptionText && <span className="em-text__caption">{menuCaptionText}</span>}
						</Typography>
					</ProfileTrigger>
				) : (
					<StyledIconButton
						size="small"
						onClick={handleClick}
						data-testid={testId + "-iconButton"}
						disableFocusRipple
						disableRipple
					>
						{startIcon}
					</StyledIconButton>
				)}
			</ThemeProvider>

			<Menu
				data-testid={testId + "-menu"}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				sx={menuSx}
			>
				{menuItems.map((menuItem: IEnhancedMenuItem | ISeparator | IHeading, index: number) => {
					const castMenuItem = menuItem as ISeparator;
					const castEnhancedMenuItem = menuItem as IEnhancedMenuItem;
					return !castMenuItem.separator ? (
						getMenuItem(castEnhancedMenuItem, index)
					) : (
						<Divider data-testid={index} key={index} />
					);
				})}
			</Menu>
		</>
	);
};

// EnhancedMenu.defaultProps = {
//   menuTitleText: "Menu Title",
//   variant: "outlined",
//   menuTitleBackgroundColor: "primary",
//   startIcon: "",
//   showEndIcon: false,
//   menuItems: [
//     {
//       label: "Menu Item 1",
//       navigateTo: "/"
//     },
//     {
//       label: "Menu Item 2",
//       navigateTo: "/"
//     },
//   ],
// };
